import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { isAfter, isSameDay } from 'date-fns';
import {
  formatISODateStr,
  hasValidStripeMlsProductId as portalHasValidStripeMlsProductId,
} from 'utils';
import {
  AnsweringSubscriptionJsonType,
  AnsweringSubscriptionLevel,
} from '@bb/domain/dist/types';
import type {
  IUserOrgResponse,
  GetPortalAnsweringConfigResponse,
} from '@/app-package';
import { UserRoles } from '@bb/users';
import { AnsweringServiceType } from '@bb/domain/dist/types/AnsweringSubscription';
import {
  createAnsweringSubscription,
  findActiveSubscription,
  CreateAnsweringSubscriptionInput,
  getEnhancedAnsweringPrices,
} from 'api/AnsweringSubscriptionController';
import { getEnabledListingCountForAppointmentDesk } from 'api/ListingController';
import { getOnboardingPortalLaunchDatesAssociations } from 'api/OnboardingPortalController';
import { getOrgUsers } from 'api/UserController';
import { AuthContext } from 'contexts';
import { message } from 'components';
import {
  getStripeCustomerId,
  getCardInfo,
  CardInfo,
} from 'api/BillingController';
import {
  PortalAssociation,
  PortalWithAssociationDataConfig,
} from '../../onboarding/types';
import { shiftToUtc } from '../../onboarding/util';
import { StyledLoading } from './StyledComponents';

const today = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
};

type OwnerState = IUserOrgResponse['users'][0];

export type EnhancedAnsweringPrice = {
  priceId: string;
  range: string;
  amount: number;
  start: number;
  end?: number;
};

export type AppointmentDeskContextType = {
  // Args
  portalConfig?: GetPortalAnsweringConfigResponse;
  globalTaxPercentage?: number | null;
  subscriptionType: AnsweringSubscriptionLevel;
  // States
  owner?: OwnerState;
  answeringSubscription?: Omit<AnsweringSubscriptionJsonType, 'startDate'> & {
    startDate: Date;
  };
  currentStep: number;
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  subscriptionExists: boolean;
  subscriptionCreated: boolean;
  brokerageLaunchDate?: string;
  defaultStartDate: Date;
  selectedStartDate: Date;
  setSelectedStartDate: React.Dispatch<React.SetStateAction<Date>>;
  createSubscription: () => void;
  billingRedirection: () => void;
  paymentMethod?: string;
  disableSubscribeBtn?: boolean;
  subscribeBtnLoading?: boolean;
  fetchStripeCustomerId: () => void;
  userId?: string;
  defaultEnabled: boolean;
  setDefaultEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  isBoardCovered?: boolean;
  association?: PortalAssociation;
  selectedSubscriptionType?: AnsweringServiceType;
  setSelectedSubscriptionType: React.Dispatch<
    React.SetStateAction<AnsweringServiceType | undefined>
  >;
  enhancedPrices: EnhancedAnsweringPrice[];
  enabledListingsCount: number;
};

const AppointmentDeskContext = React.createContext<AppointmentDeskContextType>(
  null!,
);

interface AppointmentDeskContextProviderProps {
  orgId: string;
  subscriptionType: AnsweringSubscriptionLevel;
  userId?: string;
  brokerageLaunchDate?: string;
  billingRedirection: () => void;
  isBoardCovered?: boolean;
}

export enum AppointmentDeskStep {
  MyPlan = 0,
  Activate = 1,
  Confirmation = 2,
  ManageListings = 0.5,
}

export const AppointmentDeskContextProvider: React.FC<
  AppointmentDeskContextProviderProps
> = ({
  orgId,
  subscriptionType,
  userId,
  brokerageLaunchDate,
  billingRedirection,
  children,
  isBoardCovered,
}) => {
  const { auth, answeringPortalConfig } = useContext(AuthContext);

  // Answering Subscription
  const [answeringSubscription, setAnsweringSubscription] =
    useState<AppointmentDeskContextType['answeringSubscription']>();
  const [fetchingSubscription, setFetchingSubscription] = useState(true);

  // FE component states
  const [owner, setOwner] = useState<OwnerState>(null!);
  const [currentStep, setCurrentStep] = useState<AppointmentDeskStep>(0);
  const [subscriptionExists, setSubscriptionExists] = useState(false);
  const [subscriptionCreated, setSubscriptionCreated] = useState(false);
  const [defaultStartDate, setDefaultStartDate] = useState(today());
  const [selectedStartDate, setSelectedStartDate] = useState(today());
  const [paymentMethod] = useState('');
  const [stripeCustomerId, setStripeCustomerId] = useState('');
  const [disableSubscribeBtn, setDisableSubscribeBtn] = useState(true);
  const [onboardingPortalData, setOnboardingPortalData] =
    useState<PortalWithAssociationDataConfig | null>(null);
  const [globalTaxPercentage, setGlobalTaxPercentage] = useState<
    number | undefined | null
  >();
  const [subscribeBtnLoading, setSubscribeBtnLoading] = useState(false);
  const [cardInfo, setCardInfo] = useState<CardInfo>(null);
  const [defaultEnabled, setDefaultEnabled] = useState(true);

  const [association, setAssociation] = useState<PortalAssociation>();
  const [selectedSubscriptionType, setSelectedSubscriptionType] = useState();
  const [enhancedPrices, setEnhancedPrices] = useState<
    EnhancedAnsweringPrice[]
  >([]);
  const [enabledListingsCount, setEnabledListingsCount] = useState(0);

  const hasValidStripeMlsProductId = useMemo(
    () => portalHasValidStripeMlsProductId(answeringPortalConfig?.mlsProductId),
    [answeringPortalConfig],
  );

  useEffect(() => {
    (async () => {
      try {
        const response = await getOrgUsers({ role: [UserRoles.Owner] });

        if (response?.users.length) {
          setOwner(response.users[0]);
        }
      } catch (err) {
        message.error('Error fetching org owner info');
      }
    })();
  }, []);

  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const prices = await getEnhancedAnsweringPrices();
        const newPrices = prices?.map(price => {
          const [startStr, endStr] = price.range.split('-');
          const newPrice: EnhancedAnsweringPrice = {
            ...price,
            start: parseInt(startStr, 10),
          };
          if (endStr) newPrice.end = parseInt(endStr, 10);
          return newPrice;
        });
        setEnhancedPrices(newPrices || []);
      } catch (err) {
        message.error('Error fetching enhanced answering prices');
      }
    };

    if (hasValidStripeMlsProductId) {
      fetchPrices();
    }
  }, [hasValidStripeMlsProductId]);

  useEffect(() => {
    const fetchEnabledCount = async () => {
      try {
        const enabled =
          await getEnabledListingCountForAppointmentDesk(subscriptionType);
        setEnabledListingsCount(enabled.count);
      } catch (err) {
        message.error('Error fetching enabled listing count');
      }
    };

    // Currently only used in MyPlan & Activate step
    if (
      hasValidStripeMlsProductId &&
      (currentStep === 1 || currentStep === 0)
    ) {
      fetchEnabledCount();
    }
  }, [hasValidStripeMlsProductId, currentStep, subscriptionType]);

  useEffect(() => {
    if (brokerageLaunchDate) {
      const launchUtc = shiftToUtc(brokerageLaunchDate);
      const todayIsOnOrAfterLaunch =
        isAfter(today(), launchUtc) || isSameDay(today(), launchUtc);
      // need to check if today's date is on or after the launch date. If true, UI should display that appointment desk launched today.
      // today() function is to match what is generated from back end when an appointment desk is created.

      if (!todayIsOnOrAfterLaunch) {
        setDefaultStartDate(launchUtc);
        setSelectedStartDate(launchUtc);
      }
    }
  }, [brokerageLaunchDate]);

  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const subscriptionData = await findActiveSubscription({
          subscriptionLevel: subscriptionType,
          userId,
        });
        if (subscriptionData) {
          // There was an existing subscription
          setSubscriptionExists(true);
          setAnsweringSubscription({
            ...subscriptionData,
            startDate: new Date(subscriptionData.startDate),
          });
          setDefaultEnabled(subscriptionData.defaultEnabled !== false);
          if (
            !hasValidStripeMlsProductId ||
            subscriptionData?.serviceType ===
              AnsweringServiceType.LiveConciergePlus
          ) {
            setCurrentStep(2);
          }
        }
      } catch (err) {
        message.error('Error fetching active subscription');
      }
      setFetchingSubscription(false);
    };

    fetchSubscription();
  }, [
    hasValidStripeMlsProductId,
    subscriptionType,
    orgId,
    userId,
    answeringPortalConfig,
    subscriptionCreated,
  ]);

  const fetchStripeCustomerId = useCallback(async () => {
    const customerId = await getStripeCustomerId('appointmentDesk', userId);
    setStripeCustomerId(customerId);
  }, [userId]);

  useEffect(() => {
    fetchStripeCustomerId();
  }, [fetchStripeCustomerId]);

  useEffect(() => {
    const fetchCardInfo = async () => {
      if (!stripeCustomerId) return;
      const res = await getCardInfo(stripeCustomerId);
      setCardInfo(res);
    };

    fetchCardInfo();
  }, [stripeCustomerId]);

  useEffect(() => {
    if (
      cardInfo ||
      (!hasValidStripeMlsProductId &&
        answeringPortalConfig?.appointmentDeskMlsPay) ||
      isBoardCovered ||
      (hasValidStripeMlsProductId && association?.useLiveConciergePlus)
    ) {
      setDisableSubscribeBtn(false);
    } else {
      setDisableSubscribeBtn(true);
    }
  }, [
    cardInfo,
    answeringPortalConfig?.appointmentDeskMlsPay,
    isBoardCovered,
    hasValidStripeMlsProductId,
    association,
  ]);

  useEffect(() => {
    const fetchOnboardingPortal = async () => {
      const foundOnboardingPortal =
        await getOnboardingPortalLaunchDatesAssociations(
          auth.org.onboardingPortalId!,
          auth.org.id,
        );
      if (foundOnboardingPortal) {
        setOnboardingPortalData(foundOnboardingPortal);
        setGlobalTaxPercentage(foundOnboardingPortal.globalTaxPercentage);
      }
    };
    fetchOnboardingPortal();
  }, [auth]);

  useEffect(() => {
    if (auth && onboardingPortalData?.associations) {
      if (subscriptionType === 'user') {
        // FIXME: user mls might not match auth if managing for another user
        const userPrimaryAssociationId =
          auth.mls_numbers.find(mls => mls.isPrimary)?.boardId ||
          auth.mls_numbers[0]?.boardId;
        const onboardingAssociation = onboardingPortalData.associations.find(
          a => a.id === userPrimaryAssociationId,
        );
        if (onboardingAssociation) setAssociation(onboardingAssociation);
      } else {
        const onboardingAssociation =
          onboardingPortalData.associations.find(a => a.useLiveConciergePlus) ||
          onboardingPortalData.associations.find(a => a.useAppointmentDesk) ||
          onboardingPortalData.associations[0];
        if (onboardingAssociation) setAssociation(onboardingAssociation);
      }
    }
  }, [auth, onboardingPortalData, subscriptionType]);

  const createSubscription = async () => {
    const { answeringServiceId, answeringServicePhone } = answeringPortalConfig;
    setDisableSubscribeBtn(true);
    setSubscribeBtnLoading(true);
    const submitValue: CreateAnsweringSubscriptionInput = {
      ...(userId && { userId }),
      startDate: formatISODateStr(selectedStartDate),
      subscriptionLevel: subscriptionType,
      coverageType: '24/7', // default for now, until https://brokerbay.atlassian.net/browse/APP-4157 is re-prioritized
      answeringServicePhone,
      answeringServiceId,
      stripeCustomerId,
      defaultEnabled,
      ...(hasValidStripeMlsProductId
        ? { serviceType: selectedSubscriptionType }
        : {}),
    };

    try {
      await createAnsweringSubscription(submitValue);
      setCurrentStep(2);
      setSubscriptionExists(true);
      setSubscriptionCreated(true);
    } catch (err) {
      message.error('Error subscribing to the appointment desk');
    }
    setDisableSubscribeBtn(false);
    setSubscribeBtnLoading(false);
  };

  if (fetchingSubscription) {
    return StyledLoading;
  }

  return (
    <AppointmentDeskContext.Provider
      value={{
        owner,
        answeringSubscription,
        portalConfig: answeringPortalConfig,
        globalTaxPercentage,
        currentStep,
        setCurrentStep,
        subscriptionExists,
        subscriptionCreated,
        subscriptionType,
        brokerageLaunchDate,
        defaultStartDate,
        selectedStartDate,
        setSelectedStartDate,
        createSubscription,
        paymentMethod,
        disableSubscribeBtn,
        billingRedirection,
        userId,
        fetchStripeCustomerId,
        subscribeBtnLoading,
        defaultEnabled,
        setDefaultEnabled,
        isBoardCovered,
        association,
        selectedSubscriptionType,
        setSelectedSubscriptionType,
        enhancedPrices,
        enabledListingsCount,
      }}
    >
      {children}
    </AppointmentDeskContext.Provider>
  );
};

export default AppointmentDeskContext;
